import { t as uiText } from '../i18n';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  orderBy,
  query,
  limit,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { ChatMessage } from '../hooks/useAgentChat';
import type { IngestFlowState } from '../utils/agentIngest';
import { normalizeIngestFlow } from '../utils/agentIngest';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AgentChatSessionEntry {
  sessionId: string;
  title: string | null;
  updatedAt: Date;
  messages: ChatMessage[];
  ingestFlow?: IngestFlowState | null;
  pinned?: boolean;
  pinnedAt?: Date;
}

interface AgentChatSidebarContentProps {
  workspaceId: string;
  onNewChatClick?: () => void;
  onSessionClick?: (entry: AgentChatSessionEntry) => void;
  selectedSessionId?: string | null;
}

// ---------------------------------------------------------------------------
// SessionRow sub-component
// ---------------------------------------------------------------------------

interface SessionRowProps {
  entry: AgentChatSessionEntry;
  isSelected: boolean;
  isHovered: boolean;
  onSelect: () => void;
  onHover: () => void;
  onHoverEnd: () => void;
  onTogglePin: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
}

const PinIcon: React.FC<{ filled?: boolean }> = ({ filled = false }) => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8.5 1.5L10.5 3.5L8 6L7 5.5L4.5 8.5L3 7L6 4.5L5.5 3.5L8.5 1.5Z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill={filled ? 'currentColor' : 'none'}
    />
    <path d="M3 7L1.5 10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

const SessionRow: React.FC<SessionRowProps> = ({
  entry,
  isSelected,
  isHovered,
  onSelect,
  onHover,
  onHoverEnd,
  onTogglePin,
  onDelete,
}) => {
  const dateStr = `${String(entry.updatedAt.getMonth() + 1).padStart(2, '0')}/${String(entry.updatedAt.getDate()).padStart(2, '0')} · ${String(entry.updatedAt.getHours()).padStart(2, '0')}:${String(entry.updatedAt.getMinutes()).padStart(2, '0')}`;

  return (
    <div
      style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
      onMouseEnter={onHover}
      onMouseLeave={onHoverEnd}
    >
      <button
        type="button"
        onClick={onSelect}
        style={{
          flex: 1,
          padding: '8px 12px',
          border: 'none',
          borderRadius: '8px',
          backgroundColor: isSelected ? 'var(--workspace-primary, #cfae91)' : isHovered ? 'rgba(0,0,0,0.05)' : 'transparent',
          color: isSelected ? '#fff' : '#333',
          textAlign: 'left',
          cursor: 'pointer',
          transition: 'background-color 0.15s ease, color 0.15s ease',
          minWidth: 0,
        }}
      >
        <div
          style={{
            fontSize: '13px',
            fontWeight: 600,
            lineHeight: 1.4,
            wordBreak: 'break-word',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {entry.title || `${entry.sessionId.substring(0, 12)}...`}
        </div>
        <div
          style={{
            fontSize: '11px',
            marginTop: '2px',
            color: 'inherit',
            opacity: 0.65,
            letterSpacing: '0.01em',
          }}
        >
          {dateStr}
        </div>
      </button>
      {/* Action buttons — visible on hover */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          flexShrink: 0,
          opacity: isHovered || entry.pinned ? 1 : 0,
          transition: 'opacity 0.15s ease',
          pointerEvents: isHovered ? 'auto' : 'none',
        }}
      >
        <button
          type="button"
          onClick={onTogglePin}
          title={entry.pinned ? uiText("Remove pin") : uiText("Pin this")}
          style={{
            background: 'transparent',
            border: 'none',
            color: entry.pinned ? 'var(--workspace-primary, #cfae91)' : '#adb5bd',
            cursor: 'pointer',
            padding: '4px 5px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = entry.pinned ? '#888' : 'var(--workspace-primary, #cfae91)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = entry.pinned ? 'var(--workspace-primary, #cfae91)' : '#adb5bd';
          }}
        >
          <PinIcon filled={entry.pinned} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          title={uiText("Delete")}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#adb5bd',
            fontSize: '11px',
            cursor: 'pointer',
            padding: '4px 5px',
            borderRadius: '4px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#dc3545';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#adb5bd';
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AgentChatSidebarContent: React.FC<AgentChatSidebarContentProps> = ({
  workspaceId,
  onNewChatClick,
  onSessionClick,
  selectedSessionId = null,
}) => {
  const [sessions, setSessions] = useState<AgentChatSessionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredSessionId, setHoveredSessionId] = useState<string | null>(null);
  const [isNewChatHovered, setIsNewChatHovered] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isNewChatSelected = !selectedSessionId;
  const isNewChatFilled = isNewChatSelected || isNewChatHovered;

  // ---------------------------------------------------------------------------
  // Real-time listener via onSnapshot
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!workspaceId) {
      setSessions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const q = query(
      collection(db, `workspaces/${workspaceId}/agentChatSessions`),
      orderBy('updatedAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const list: AgentChatSessionEntry[] = snap.docs.map((d) => {
          const data = d.data() as any;
          return {
            sessionId: d.id,
            title: data.title ?? null,
            updatedAt: data.updatedAt?.toDate?.() ?? new Date(),
            messages: (data.messages ?? []) as ChatMessage[],
            ingestFlow: normalizeIngestFlow(data.ingestFlow),
            pinned: data.pinned === true,
            pinnedAt: data.pinnedAt?.toDate?.() ?? undefined,
          };
        });
        setSessions(list);
        setLoading(false);
      },
      (err) => {
        console.error('[AgentChatSidebarContent] onSnapshot error', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [workspaceId]);

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------

  const handleDelete = useCallback(
    async (e: React.MouseEvent, sessionId: string) => {
      e.stopPropagation();
      try {
        await deleteDoc(doc(db, `workspaces/${workspaceId}/agentChatSessions`, sessionId));
        setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
      } catch (err) {
        console.error('[AgentChatSidebarContent] delete error', err);
      }
    },
    [workspaceId]
  );

  // ---------------------------------------------------------------------------
  // Pin toggle
  // ---------------------------------------------------------------------------

  const handleTogglePin = useCallback(
    async (e: React.MouseEvent, sessionId: string, currentlyPinned: boolean) => {
      e.stopPropagation();
      try {
        const ref = doc(db, `workspaces/${workspaceId}/agentChatSessions`, sessionId);
        if (currentlyPinned) {
          await updateDoc(ref, { pinned: false, pinnedAt: deleteField() });
        } else {
          await updateDoc(ref, { pinned: true, pinnedAt: serverTimestamp() });
        }
      } catch (err) {
        console.error('[AgentChatSidebarContent] toggle pin error', err);
      }
    },
    [workspaceId]
  );

  // ---------------------------------------------------------------------------
  // Filter + split pinned / unpinned
  // ---------------------------------------------------------------------------

  const displayed = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter(
      (s) =>
        s.title?.toLowerCase().includes(q) ||
        s.sessionId.toLowerCase().includes(q)
    );
  }, [sessions, searchQuery]);

  const pinnedDisplayed = useMemo(
    () =>
      displayed
        .filter((s) => s.pinned)
        .sort((a, b) => (b.pinnedAt?.getTime() ?? 0) - (a.pinnedAt?.getTime() ?? 0)),
    [displayed]
  );

  const unpinnedDisplayed = useMemo(
    () => displayed.filter((s) => !s.pinned),
    [displayed]
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div
      style={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header: New Chat + Search */}
      <div style={{ flexShrink: 0, padding: '16px 16px 12px' }}>
        <button
          type="button"
          onClick={onNewChatClick}
          aria-pressed={isNewChatSelected}
          style={{
            width: '100%',
            padding: '10px 16px',
            border: '1.5px solid var(--workspace-primary, #cfae91)',
            borderRadius: '8px',
            backgroundColor: isNewChatFilled ? 'var(--workspace-primary, #cfae91)' : 'transparent',
            color: isNewChatFilled ? '#fff' : 'var(--workspace-primary, #cfae91)',
            fontSize: '14px',
            fontWeight: 600,
            textAlign: 'left',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
          onMouseEnter={() => setIsNewChatHovered(true)}
          onMouseLeave={() => setIsNewChatHovered(false)}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
            <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          {uiText("New chat")}
        </button>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={uiText("Search history")}
          style={{
            width: '100%',
            marginTop: '10px',
            padding: '10px 14px',
            border: '1px solid #ddd',
            borderRadius: '10px',
            backgroundColor: '#fff',
            color: '#333',
            fontSize: '13px',
            outline: 'none',
            boxSizing: 'border-box',
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = 'var(--workspace-primary, #cfae91)';
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = '#ddd';
          }}
        />
      </div>

      {/* Session list */}
      <div
        ref={scrollContainerRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '0 16px 16px',
        }}
      >
        {/* ピン止め section */}
        {!loading && pinnedDisplayed.length > 0 && (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginBottom: '8px',
                paddingTop: '12px',
                borderTop: '1px solid rgba(0,0,0,0.08)',
                fontSize: '11px',
                fontWeight: 700,
                color: '#888',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.5 1.5L10.5 3.5L8 6L7 5.5L4.5 8.5L3 7L6 4.5L5.5 3.5L8.5 1.5Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="currentColor"/>
                <path d="M3 7L1.5 10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
              {uiText("Pin this")}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '4px' }}>
              {pinnedDisplayed.map((entry) => (
                <SessionRow
                  key={entry.sessionId}
                  entry={entry}
                  isSelected={selectedSessionId === entry.sessionId}
                  isHovered={hoveredSessionId === entry.sessionId}
                  onSelect={() => onSessionClick?.(entry)}
                  onHover={() => setHoveredSessionId(entry.sessionId)}
                  onHoverEnd={() => setHoveredSessionId(null)}
                  onTogglePin={(e) => handleTogglePin(e, entry.sessionId, true)}
                  onDelete={(e) => handleDelete(e, entry.sessionId)}
                />
              ))}
            </div>
          </>
        )}

        {/* 履歴 section */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginBottom: '8px',
            paddingTop: '12px',
            borderTop: '1px solid rgba(0,0,0,0.08)',
            fontSize: '11px',
            fontWeight: 700,
            color: '#888',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M6 3.5V6L7.5 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {uiText("History")}
        </div>

        {loading ? (
          <div style={{ fontSize: '13px', color: '#888' }}>{uiText("Loading now...")}</div>
        ) : unpinnedDisplayed.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#888' }}>
            {sessions.length === 0 ? uiText("No history yet") : pinnedDisplayed.length > 0 ? uiText("All items are already pinned") : uiText("No matching history")}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {unpinnedDisplayed.map((entry) => (
              <SessionRow
                key={entry.sessionId}
                entry={entry}
                isSelected={selectedSessionId === entry.sessionId}
                isHovered={hoveredSessionId === entry.sessionId}
                onSelect={() => onSessionClick?.(entry)}
                onHover={() => setHoveredSessionId(entry.sessionId)}
                onHoverEnd={() => setHoveredSessionId(null)}
                onTogglePin={(e) => handleTogglePin(e, entry.sessionId, false)}
                onDelete={(e) => handleDelete(e, entry.sessionId)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentChatSidebarContent;
